import React from 'react';
import { Package } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import type { PreloadedIngredient } from '@/types/purchase-order.types';

interface DatabaseSupplier {
  id: string;
  businessName: string;
  isActive: boolean;
}

interface PurchaseOrderIngredientsTableProps {
  ingredients: PreloadedIngredient[];
  suppliers: DatabaseSupplier[];
  onToggle: (ingredientId: number, isSelected: boolean) => void;
  onQuantityChange: (ingredientId: number, quantity: number) => void;
  selectedIngredientFilter: string;
}

type SortField = 'supplier' | 'name' | 'currentStock' | 'minStock' | 'consumption' | 'unitCost' | 'quantity' | 'total';

export const PurchaseOrderIngredientsTable: React.FC<PurchaseOrderIngredientsTableProps> = ({
  ingredients,
  suppliers,
  onToggle,
  onQuantityChange,
  selectedIngredientFilter
}) => {
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    supplier: 'Proveedor',
    name: 'Insumo',
    currentStock: 'Stock Actual',
    minStock: 'Stock Min.',
    consumption: 'Consumo',
    unitCost: 'Costo Unit.',
    quantity: 'Cantidad',
    total: 'Total'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (ingredient: PreloadedIngredient, field: SortField): any => {
    switch (field) {
      case 'supplier':
        return (ingredient.ingredient.supplierName || 'N/A').toLowerCase();
      case 'name':
        return ingredient.ingredient.name.toLowerCase();
      case 'currentStock':
        return ingredient.ingredient.currentStock || 0;
      case 'minStock':
        return ingredient.ingredient.minStock || 0;
      case 'consumption':
        // Simular consumo diario (esto debería venir de datos reales)
        const hash = String(ingredient.ingredient.id).split('').reduce((a, b) => {
          a = ((a << 5) - a) + b.charCodeAt(0);
          return a & a;
        }, 0);
        return (Math.abs(hash) % 5) + 1; // Entre 1 y 5 unidades por día
      case 'unitCost':
        return ingredient.unitCost;
      case 'quantity':
        return ingredient.currentQuantity;
      case 'total':
        return ingredient.currentQuantity * ingredient.unitCost;
      default:
        return ingredient.ingredient.name;
    }
  };

  // Filtrar ingredientes basado en el filtro seleccionado
  const getFilteredIngredients = () => {
    if (selectedIngredientFilter === 'all') {
      return ingredients;
    }
    if (selectedIngredientFilter === 'selected') {
      return ingredients.filter(item => item.isSelected);
    }
    if (selectedIngredientFilter === 'low-stock') {
      return ingredients.filter(item =>
        (item.ingredient.currentStock || 0) <= (item.ingredient.minStock || 0)
      );
    }
    return ingredients;
  };

  // Usar el hook reutilizable
  const tableData = useTableData<PreloadedIngredient, SortField>(
    {
      data: getFilteredIngredients(),
      defaultSortField: 'name',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  // Simular consumo diario (esto debería venir de datos reales)
  const getDailyConsumption = (ingredient: PreloadedIngredient) => {
    const hash = String(ingredient.ingredient.id).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return (Math.abs(hash) % 5) + 1; // Entre 1 y 5 unidades por día
  };

  // Obtener el stock mínimo real desde los datos del ingrediente
  const getStockMinimo = (ingredient: PreloadedIngredient) => {
    return ingredient.ingredient.minStock || 0;
  };

  if (getFilteredIngredients().length === 0) {
    return (
      <div className="text-center py-8">
        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-600 mb-2">No hay ingredientes</h3>
        <p className="text-gray-500">No se encontraron ingredientes que coincidan con la búsqueda</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Checkbox
                  checked={tableData.pagination.paginatedData.every(item => item.isSelected)}
                  onCheckedChange={(checked) => {
                    tableData.pagination.paginatedData.forEach(item => {
                      onToggle(item.ingredient.id, !!checked);
                    });
                  }}
                />
              </th>
              <SortableHeader
                field="supplier"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Proveedor
              </SortableHeader>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Insumo
              </SortableHeader>
              <SortableHeader
                field="currentStock"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Stock actual
              </SortableHeader>
              <SortableHeader
                field="minStock"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Stock Min.
              </SortableHeader>
              <SortableHeader
                field="consumption"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Consumo
              </SortableHeader>
              <SortableHeader
                field="unitCost"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Costo Unit.
              </SortableHeader>
              <SortableHeader
                field="quantity"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center bg-blue-50"
              >
                Cantidad
              </SortableHeader>
              <SortableHeader
                field="total"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Total
              </SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map(item => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <Checkbox
                    checked={item.isSelected}
                    onCheckedChange={(checked) => onToggle(item.ingredient.id, !!checked)}
                  />
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="text-sm text-gray-900">
                    {item.ingredient.supplierName || 'N/A'}
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {item.ingredient.name}
                    </div>
                    {item.ingredient.description && (
                      <div className="text-sm text-gray-500">
                        {item.ingredient.description}
                      </div>
                    )}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="text-sm text-gray-900">
                    {item.ingredient.currentStock || 0} {item.ingredient.unit}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="text-sm text-gray-900">
                    {getStockMinimo(item)} {item.ingredient.unit}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="text-sm text-gray-900">
                    {getDailyConsumption(item)} {item.ingredient.unit}/día
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="text-sm text-gray-900">
                    {formatCurrency(item.unitCost)}
                  </div>
                </td>

                <td className="px-4 py-4 text-center bg-blue-50">
                  <div className="flex items-center justify-center">
                    <Input
                      type="number"
                      value={item.currentQuantity}
                      onChange={(e) => onQuantityChange(item.ingredient.id, Math.max(0, parseInt(e.target.value) || 0))}
                      disabled={!item.isSelected}
                      className="w-20 text-center"
                      min="0"
                    />
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(item.currentQuantity * item.unitCost)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};